import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { LogsApiService, ActivityLogsResponse, ActivityLog } from 'src/app/core/services/api/logs-api.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface LogEntry {
  id?: string; // Use performed_at + causer_name as unique ID if needed
  name: string;
  ipAddress: string;
  timestamp: string;
  action: string;
}

@Component({
  selector: 'app-logs',
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.css']
})
export class LogsComponent implements OnInit, OnDestroy {
  logs: LogEntry[] = [];
  isLoading = false;
  currentPage = 1;
  totalPages = 1;
  private destroy$ = new Subject<void>();

  constructor(
    private logsApiService: LogsApiService,
    private authService: AuthService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loadActivityLogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadActivityLogs(page: number = 1): void {
    this.isLoading = true;
    
    try {
      // Try to get dealer ID from route parameter first (for dealer-details pages)
      const dealerId = this.route.parent?.snapshot.paramMap.get('id');
      
      let userId: string | undefined;
      
      if (dealerId) {
        // If viewing from dealer-details page, use the dealer ID
        userId = dealerId;
        console.log(`Fetching activity logs for dealer ID: ${userId}, page: ${page}`);
      } else {
        // Otherwise, fall back to the current logged-in user's ID
        const userObj = this.authService.getUserObj();
        userId = userObj?.id;
        const userType = this.authService.getUserType();
        console.log(`Fetching activity logs for ${userType} with ID: ${userId}, page: ${page}`);
      }

      if (!userId) {
        console.warn('User ID not found');
        this.isLoading = false;
        return;
      }

      // Call the API to get activity logs
      // Works for Brokers, Agents, and Dealers
      this.logsApiService.getUserAgencyActivityLogs(userId, { page })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: ActivityLogsResponse) => {
            this.isLoading = false;
            
            // Handle API response
            if (response && response.data && Array.isArray(response.data)) {
              // Map API response to LogEntry format
              this.logs = response.data.map((log: ActivityLog) => ({
                id: `${log.performed_at}-${log.causer_name}`, // Create unique ID
                name: log.causer_name,
                ipAddress: log.ip_address,
                timestamp: this.formatTimestamp(log.performed_at),
                action: log.description
              }));

              // Update pagination info
              if (response.meta) {
                this.currentPage = response.meta.current_page;
                this.totalPages = response.meta.last_page;
              }

              console.log(`Loaded ${this.logs.length} activity logs from page ${this.currentPage} of ${this.totalPages}`);
            } else {
              console.warn('Unexpected API response format', response);
              this.logs = [];
            }
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Error fetching activity logs:', error);
            // Keep the component functional even if API fails
            this.logs = [];
          }
        });
    } catch (error) {
      this.isLoading = false;
      console.error('Error in loadActivityLogs:', error);
    }
  }

  /**
   * Formats the timestamp from the API (MM/DD/YYYY HH:MM:SS format)
   * to a more readable format or converts to ISO format for consistency
   */
  private formatTimestamp(timestamp: string): string {
    try {
      // API returns format like "11/21/2025 14:22:04"
      // Keep it as is for display, or convert to ISO if needed
      return timestamp;
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return timestamp;
    }
  }

  /**
   * Load next page of logs
   */
  loadNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.loadActivityLogs(this.currentPage + 1);
    }
  }

  /**
   * Load previous page of logs
   */
  loadPreviousPage(): void {
    if (this.currentPage > 1) {
      this.loadActivityLogs(this.currentPage - 1);
    }
  }
}
